import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "../../services/api";
import { getPublicCertification } from "../../services/certificationApi";
import { formatEnglishDate } from "../../utils/dateFormatting";
import LevelBadge from "../certification/LevelBadge";
import BadgesGrid from "../certification/BadgesGrid";
import EvidenceList from "../certification/EvidenceList";
import IPFSLinks from "../certification/IPFSLinks";
import CertificationQRCode from "../certification/CertificationQRCode";
import AdminCertifyButton from "./AdminCertifyButton";
import AdminCertifyOffChainButton from "./AdminCertifyOffChainButton";
import AdminDecertifyButton from "./AdminDecertifyButton";
import AdminRevokeButton from "./AdminRevokeButton";
import { 
  QUESTIONS_BY_ID, 
  enrichAnswersWithMetadata,
  getQuestionsBySubsection 
} from "../../shared/esgQuestions";
import { getMaxPoints, SCORE_VERSION } from "../../esg/scoringV1";

/**
 * Componente per visualizzare il dettaglio di una richiesta ESG (Admin)
 * @param {object} props
 * @param {string} props.adminKey - Chiave admin per autenticazione
 * @param {string} props.walletAddress - Indirizzo wallet dell'admin
 */
const AdminESGDetail = ({ adminKey, walletAddress }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [notes, setNotes] = useState("");
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  
  // Scoring state
  const [scoring, setScoring] = useState({});
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringTotals, setScoringTotals] = useState(null);
  const [publicCertification, setPublicCertification] = useState(null);
  const [publicCertificationLoading, setPublicCertificationLoading] = useState(false);
  const [publicCertificationError, setPublicCertificationError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [logoRetryAttempted, setLogoRetryAttempted] = useState(false);

  const VISUAL_TO_BACKEND_ID = React.useMemo(() => {
    const map = new Map();
    QUESTIONS_BY_ID.forEach((question) => {
      if (question?.visualNumber && question?.id) {
        map.set(question.visualNumber, question.id);
      }
    });
    return map;
  }, []);

  const normalizeTotals = (totals) => {
    if (!totals || typeof totals !== "object") return null;
    const E = Number(totals.E ?? totals.environmental ?? totals.env ?? 0) || 0;
    const S = Number(totals.S ?? totals.social ?? totals.soc ?? 0) || 0;
    const G = Number(totals.G ?? totals.governance ?? totals.gov ?? 0) || 0;
    const total = Number(totals.total ?? (E + S + G)) || 0;
    return { E, S, G, total };
  };

  const normalizeByQuestion = (rawByQuestion) => {
    if (!rawByQuestion || typeof rawByQuestion !== "object") return {};

    const normalized = {};
    Object.entries(rawByQuestion).forEach(([rawKey, data]) => {
      const backendKey = QUESTIONS_BY_ID.has(rawKey)
        ? rawKey
        : (VISUAL_TO_BACKEND_ID.get(rawKey) || null);

      if (!backendKey) return;

      if (typeof data === "number" || typeof data === "string") {
        normalized[backendKey] = {
          score: Number(data ?? 0) || 0,
          evidenceVerified: false,
          notes: "",
        };
        return;
      }

      normalized[backendKey] = {
        score: Number(data?.score ?? 0) || 0,
        evidenceVerified: Boolean(data?.evidenceVerified),
        notes: data?.notes || "",
      };
    });

    return normalized;
  };

  const normalizeRequest = (data) => {
    if (!data || typeof data !== "object") return data;

    let answers = data.answers;

    if (!answers || (Array.isArray(answers) && answers.length === 0)) {
      if (Array.isArray(data.esgAnswers)) {
        answers = data.esgAnswers;
      } else if (Array.isArray(data.request?.answers)) {
        answers = data.request.answers;
      } else if (Array.isArray(data.submission?.answers)) {
        answers = data.submission.answers;
      } else if (Array.isArray(data.data?.answers)) {
        answers = data.data.answers;
      }
    }

    if (Array.isArray(answers)) {
      answers = answers.map((item) => {
        if (item && !item.questionKey && item.question_key) {
          return { ...item, questionKey: item.question_key };
        }
        return item;
      });
    }

    return {
      ...data,
      answers: Array.isArray(answers) ? answers : data.answers || [],
    };
  };

  // Carica il dettaglio della richiesta
  const loadRequest = async () => {
    if (!adminKey || !id) return;

    setLoading(true);
    setError("");

    try {
      const data = await adminApi.getRequestDetail(id, adminKey, walletAddress);
      console.log("🔍 Admin ESG Detail - Data received:", data);
      console.log("� Wallet Address from backend:", data.walletAddress);
      console.log("�📋 Answers:", data.answers);
      console.log("📋 Answers type:", Array.isArray(data.answers) ? 'Array' : typeof data.answers);
      const normalized = normalizeRequest(data);
      setRequest(normalized);
      
      // Load scoring data
      await loadScoring();
    } catch (err) {
      console.error("Error loading request:", err);
      setError(err.message || "Failed to load request");
    } finally {
      setLoading(false);
    }
  };

  // Carica lo scoring
  const loadScoring = async () => {
    if (!adminKey || !id) return;

    try {
      const data = await adminApi.getScoring(id, adminKey, walletAddress);
      console.log("📊 Scoring data received:", data);

      if (data) {
        const byQuestion = data.byQuestion || data.scoring || {};
        setScoring(normalizeByQuestion(byQuestion));

        const normalizedTotals = normalizeTotals(data.totals);
        if (normalizedTotals) {
          setScoringTotals(normalizedTotals);
        }
      }
    } catch (err) {
      console.log("No existing scoring found or error:", err.message);
      // Initialize empty scoring for all questions
      setScoring({});
    }
  };

  // 🎯 Extraction helpers - robust fallback chains for various backend formats
  const resolveCertificationId = (data) =>
    data?.certificationId ||
    data?.certification?.certificationId ||
    data?.certification?.id ||
    data?.certification?.publicId ||
    data?.publicCertificationId ||
    null;

  /**
   * Estrae il JSON CID da varie sorgenti (camelCase, snake_case, nested)
   * Backend può ritornare i dati in vari formati, questo fa un'estrazione robusta
   */
  const extractJsonCid = (data) => {
    if (!data) return null;
    // camelCase
    if (data.ipfsJsonCid) return data.ipfsJsonCid;
    // snake_case
    if (data.ipfs_json_cid) return data.ipfs_json_cid;
    // Nested in certification object
    if (data.certification?.ipfsJsonCid) return data.certification.ipfsJsonCid;
    if (data.certification?.ipfs_json_cid) return data.certification.ipfs_json_cid;
    // Metodi alternativi per i dati
    if (data.reportCID) return data.reportCID;
    if (data.report_cid) return data.report_cid;
    if (data.certification?.reportCID) return data.certification.reportCID;
    if (data.certification?.report_cid) return data.certification.report_cid;
    return null;
  };

  /**
   * Estrae il PDF CID da varie sorgenti (camelCase, snake_case, nested)
   */
  const extractPdfCid = (data) => {
    if (!data) return null;
    // camelCase
    if (data.ipfsPdfCid) return data.ipfsPdfCid;
    // snake_case
    if (data.ipfs_pdf_cid) return data.ipfs_pdf_cid;
    // Nested in certification object
    if (data.certification?.ipfsPdfCid) return data.certification.ipfsPdfCid;
    if (data.certification?.ipfs_pdf_cid) return data.certification.ipfs_pdf_cid;
    // Metodi alternativi
    if (data.pdfCID) return data.pdfCID;
    if (data.pdf_cid) return data.pdf_cid;
    if (data.certification?.pdfCID) return data.certification.pdfCID;
    if (data.certification?.pdf_cid) return data.certification.pdf_cid;
    return null;
  };

  /**
   * Estrae il timestamp di pubblicazione
   */
  const extractLastPublishedAt = (data) => {
    if (!data) return null;
    if (data.lastPublishedAt) return data.lastPublishedAt;
    if (data.last_published_at) return data.last_published_at;
    if (data.publishedAt) return data.publishedAt;
    if (data.published_at) return data.published_at;
    if (data.certifiedAt) return data.certifiedAt;
    if (data.certified_at) return data.certified_at;
    if (data.certification?.lastPublishedAt) return data.certification.lastPublishedAt;
    if (data.certification?.last_published_at) return data.certification.last_published_at;
    if (data.certification?.publishedAt) return data.certification.publishedAt;
    if (data.certification?.certifiedAt) return data.certification.certifiedAt;
    return null;
  };

  /**
   * Estrae le gateway URL complete dal backend (se disponibili)
   * Il backend può ritornare:
   * - ipfsGatewayUrls.json / ipfsGatewayUrls.pdf (preferred)
   * - ipfsJsonUrl / ipfsPdfUrl (alternative)
   */
  const extractJsonUrl = (data) => {
    if (!data) return null;
    // Nested gateway URLs
    if (data.ipfsGatewayUrls?.json) return data.ipfsGatewayUrls.json;
    if (data.certification?.ipfsGatewayUrls?.json) return data.certification.ipfsGatewayUrls.json;
    // Flat URLs
    if (data.ipfsJsonUrl) return data.ipfsJsonUrl;
    if (data.certification?.ipfsJsonUrl) return data.certification.ipfsJsonUrl;
    return null;
  };

  const extractPdfUrl = (data) => {
    if (!data) return null;
    // Nested gateway URLs
    if (data.ipfsGatewayUrls?.pdf) return data.ipfsGatewayUrls.pdf;
    if (data.certification?.ipfsGatewayUrls?.pdf) return data.certification.ipfsGatewayUrls.pdf;
    // Flat URLs
    if (data.ipfsPdfUrl) return data.ipfsPdfUrl;
    if (data.certification?.ipfsPdfUrl) return data.certification.ipfsPdfUrl;
    return null;
  };

  const loadPublicCertification = async (certificationId) => {
    if (!certificationId) return;
    setPublicCertificationLoading(true);
    setPublicCertificationError("");
    try {
      const result = await getPublicCertification(certificationId);
      setPublicCertification(result);
    } catch (err) {
      // For off-chain certifications just created, the public endpoint might not have the data yet
      // This is OK - we'll use the data from the admin API instead
      console.log("ℹ️ Note: Public certification endpoint not available (off-chain cert just created)");
      // Don't show error - public data will be loaded from request.certification
    } finally {
      setPublicCertificationLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
  }, [id, adminKey]);

  useEffect(() => {
    const certificationId = resolveCertificationId(request);
    if (certificationId) {
      loadPublicCertification(certificationId);
    }
  }, [request]);

  // Download singolo allegato
  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const { downloadUrl } = await adminApi.getPresignedDownloadUrl(
        attachmentId,
        adminKey,
        walletAddress
      );
      // Apri in nuova tab
      window.open(downloadUrl, "_blank");
    } catch (err) {
      console.error("Error downloading attachment:", err);
      window.alert(`Error: ${err.message}`);
    }
  };

  // Aggiunge un commento
  const handleAddComment = async () => {
    if (!commentText.trim()) {
      window.alert("Inserisci un commento prima di inviare");
      return;
    }

    setCommentLoading(true);
    try {
      await adminApi.addComment(id, commentText, adminKey, walletAddress);
      setCommentText("");
      await loadRequest(); // Ricarica per aggiornare i commenti
      setActionMessage("✓ Commento aggiunto con successo");
      setTimeout(() => setActionMessage(""), 3000);
    } catch (err) {
      console.error("Error adding comment:", err);
      window.alert(`Error: ${err.message}`);
    } finally {
      setCommentLoading(false);
    }
  };

  // Download ZIP
  const handleDownloadZip = () => {
    const zipUrl = adminApi.getZipDownloadUrl(id, adminKey);
    window.open(zipUrl, "_blank");
  };

  // Azioni status
  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this request?")) return;

    setActionLoading(true);
    setActionMessage("");

    try {
      await adminApi.approveRequest(id, adminKey, walletAddress);
      const companyEmail = request?.email || "l'azienda";
      setActionMessage(`✓ Richiesta approvata con successo. Email di notifica inviata a ${companyEmail}.`);
      setNotes("");
      await loadRequest(); // Ricarica i dati
    } catch (err) {
      setActionMessage(`✗ Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!notes || notes.trim() === "") {
      window.alert("Il motivo del rifiuto è obbligatorio. Inserisci il motivo prima di procedere.");
      return;
    }

    if (!window.confirm("Are you sure you want to reject this request?")) return;

    setActionLoading(true);
    setActionMessage("");

    try {
      await adminApi.rejectRequest(id, notes.trim(), adminKey, walletAddress);
      const companyEmail = request?.email || "l'azienda";
      setActionMessage(`✓ Richiesta rifiutata. Email di notifica con motivo inviata a ${companyEmail}.`);
      setNotes("");
      await loadRequest();
    } catch (err) {
      setActionMessage(`✗ Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestMoreInfo = async () => {
    if (!notes || notes.trim() === "") {
      window.alert("Il messaggio con la richiesta di informazioni è obbligatorio. Specifica quali informazioni sono necessarie.");
      return;
    }

    setActionLoading(true);
    setActionMessage("");

    try {
      await adminApi.requestMoreInfo(id, notes.trim(), adminKey, walletAddress);
      const companyEmail = request?.email || "l'azienda";
      setActionMessage(`✓ Richiesta informazioni inviata. Email di notifica inviata a ${companyEmail}.`);
      setNotes("");
      await loadRequest();
    } catch (err) {
      setActionMessage(`✗ Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Elimina la richiesta
  const handleDeleteRequest = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare questa richiesta? Questa azione è irreversibile.")) return;

    setActionLoading(true);
    setActionMessage("");

    try {
      await adminApi.deleteRequest(id, adminKey, walletAddress);
      setActionMessage("✓ Richiesta eliminata con successo");
      setTimeout(() => {
        navigate("/admin/esg"); // Torna alla lista
      }, 1500);
    } catch (err) {
      setActionMessage(`✗ Error: ${err.message}`);
      setActionLoading(false);
    }
  };

  // Aggiorna lo stato
  const handleUpdateStatus = async (newStatus) => {
    const statusNotes = window.prompt(`Aggiungi note per il cambio di stato a ${newStatus.toUpperCase()}:`);
    if (statusNotes === null) return; // Annullato

    setActionLoading(true);
    setActionMessage("");

    try {
      const response = await adminApi.updateRequestStatus(id, newStatus, statusNotes || "", adminKey, walletAddress);
      
      // Gestisce il caso in cui lo status non cambia
      if (response.statusUnchanged) {
        setActionMessage(`ℹ️ Status già impostato su ${newStatus.toUpperCase()}. Nessuna modifica effettuata.`);
      } else {
        const companyEmail = request?.email || "l'azienda";
        setActionMessage(`✓ Status aggiornato a ${newStatus.toUpperCase()}. Email di notifica inviata a ${companyEmail}.`);
      }
      
      await loadRequest(); // Ricarica i dati
    } catch (err) {
      setActionMessage(`✗ Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Aggiorna il punteggio per una domanda
  const handleScoreChange = (questionId, field, value) => {
    setScoring(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  // Salva lo scoring
  const handleSaveScoring = async () => {
    setScoringLoading(true);
    setActionMessage("");

    try {
      // Build scoring maps in both formats for backend compatibility
      // Include ALL questions with data, not just those with scores
      const scoringByBackendKeyFull = {};
      const scoringByBackendKeyMinimal = {};
      const scoringByBackendKeyNumeric = {};
      const scoringByShortKeyFull = {};
      const scoringByShortKeyMinimal = {};
      const scoringByShortKeyNumeric = {};
      
      Object.entries(scoring).forEach(([questionId, data]) => {
        if (!data) return;

        const backendKey = QUESTIONS_BY_ID.has(questionId)
          ? questionId
          : (VISUAL_TO_BACKEND_ID.get(questionId) || null);

        if (!backendKey) return;

        // Check if this question has ANY relevant data
        const hasScore = data.score !== undefined && data.score !== null && data.score !== "";
        const hasEvidence = data.evidenceVerified === true;
        const hasNotes = data.notes && data.notes.trim() !== "";

        // Only include if there's at least one piece of data
        if (!hasScore && !hasEvidence && !hasNotes) return;

        const shortKey = backendKey.match(/^q\d{2}/)?.[0] || null;
        
        // Only process numeric score if it exists and is valid
        let integerScore = hasScore ? Math.round(Number(data.score)) : null;
        const isValidScore = integerScore !== null && Number.isFinite(integerScore) && integerScore > 0;

        // Full object always includes evidence and notes
        const scoringItemFull = {
          ...(isValidScore && { score: integerScore }),
          evidenceVerified: hasEvidence,
          notes: data.notes || ""
        };

        // Minimal object - only score if valid
        const scoringItemMinimal = {
          ...(isValidScore && { score: integerScore }),
          evidenceVerified: hasEvidence,
        };

        scoringByBackendKeyFull[backendKey] = scoringItemFull;
        scoringByBackendKeyMinimal[backendKey] = scoringItemMinimal;
        
        if (isValidScore) {
          scoringByBackendKeyNumeric[backendKey] = integerScore;
        }

        if (shortKey) {
          scoringByShortKeyFull[shortKey] = scoringItemFull;
          scoringByShortKeyMinimal[shortKey] = scoringItemMinimal;
          if (isValidScore) {
            scoringByShortKeyNumeric[shortKey] = integerScore;
          }
        }
      });

      if (Object.keys(scoringByBackendKeyFull).length === 0) {
        throw new Error("Inserisci almeno uno score, evidence o note prima di salvare.");
      }

      const payloadCandidates = [
        // Try short keys with FULL objects first (includes evidenceVerified)
        { submissionId: id, byQuestion: scoringByShortKeyFull },
        { submissionId: id, scoreVersion: SCORE_VERSION, byQuestion: scoringByShortKeyFull },
        // Then short keys with minimal object
        { submissionId: id, byQuestion: scoringByShortKeyMinimal },
        { submissionId: id, scoreVersion: SCORE_VERSION, byQuestion: scoringByShortKeyMinimal },
        // Then short keys with numeric values (if only scores)
        { submissionId: id, byQuestion: scoringByShortKeyNumeric },
        { submissionId: id, scoreVersion: SCORE_VERSION, byQuestion: scoringByShortKeyNumeric },
        // Finally try long keys (legacy fallback)
        { submissionId: id, byQuestion: scoringByBackendKeyFull },
        { submissionId: id, scoreVersion: SCORE_VERSION, byQuestion: scoringByBackendKeyFull },
        { submissionId: id, byQuestion: scoringByBackendKeyMinimal },
        { submissionId: id, byQuestion: scoringByBackendKeyNumeric },
      ];

      let result = null;
      let lastError = null;
      let successfulPayloadMap = null;

      for (const scoringData of payloadCandidates) {
        if (!scoringData.byQuestion && !scoringData.scoring) continue;
        const currentMap = scoringData.byQuestion || scoringData.scoring;
        if (!currentMap || Object.keys(currentMap).length === 0) continue;

        try {
          console.log("📊 Sending scoring data:", scoringData);
          result = await adminApi.saveScoring(scoringData, adminKey, walletAddress);
          successfulPayloadMap = currentMap;
          break;
        } catch (err) {
          lastError = err;
          const errMsg = String(err?.message || "").toLowerCase();
          const isValidationError = err?.status === 400 || errMsg.includes("validation");

          if (!isValidationError) {
            throw err;
          }

          console.warn("[Scoring Save] Payload rejected, trying next format", {
            status: err?.status,
            message: err?.message,
            details: err?.data,
            attemptedKeys: Object.keys(currentMap || {}),
            attemptedShape: scoringData.byQuestion ? "byQuestion" : "scoring",
          });
        }
      }

      if (!result) {
        throw lastError || new Error("Validation failed");
      }

      console.log("✅ Scoring saved successfully:", result);

      const expectedByQuestion = normalizeByQuestion(successfulPayloadMap || scoringByBackendKeyFull);
      // Verify persistence from backend (source of truth) with retries
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      const retryDelays = [0, 700, 1500, 2500];
      let persistenceConfirmed = false;
      let lastConfirmed = null;
      let lastMismatches = [];

      const computeMismatches = (confirmedByQuestion) => {
        return Object.entries(expectedByQuestion).filter(([key, expected]) => {
          const actual = confirmedByQuestion[key] || {};
          const expectedScore = Number(expected?.score ?? 0) || 0;
          const actualScore = Number(actual?.score ?? 0) || 0;
          const expectedEvidence = Boolean(expected?.evidenceVerified);
          const actualEvidence = Boolean(actual?.evidenceVerified);
          const expectedNotes = (expected?.notes || "").trim();
          const actualNotes = (actual?.notes || "").trim();

          return expectedScore !== actualScore || expectedEvidence !== actualEvidence || expectedNotes !== actualNotes;
        });
      };

      for (const delay of retryDelays) {
        if (delay > 0) await sleep(delay);

        const confirmed = await adminApi.getScoring(id, adminKey, walletAddress);
        const confirmedByQuestion = normalizeByQuestion(confirmed?.byQuestion || confirmed?.scoring || {});
        const mismatches = computeMismatches(confirmedByQuestion);

        lastConfirmed = confirmed;
        lastMismatches = mismatches;

        if (mismatches.length === 0) {
          persistenceConfirmed = true;
          break;
        }
      }

      if (!persistenceConfirmed) {
        console.error("❌ Backend persistence mismatch after save", {
          submissionId: id,
          mismatches: lastMismatches,
          expectedByQuestion,
          confirmedRaw: lastConfirmed,
        });
        throw new Error("Save non confermato dal backend: i valori letti dal backend non coincidono con quelli inviati.");
      }

      if (result.totals) {
        setScoringTotals(normalizeTotals(result.totals));
      }

      // Source of truth: reload scoring from backend after save
      await loadScoring();

      setActionMessage("✓ Scoring salvato con successo");
      setTimeout(() => setActionMessage(""), 3000);
    } catch (err) {
      console.error("❌ Error saving scoring:", err);
      console.error("❌ Error saving scoring details:", err?.data || err);
      const validationErrors = err?.data?.details?.validationErrors;
      const validationPreview = Array.isArray(validationErrors)
        ? validationErrors
            .slice(0, 3)
            .map((item) => {
              if (!item) return "";
              if (typeof item === "string") return item;
              return item?.message || item?.field || item?.path || JSON.stringify(item);
            })
            .filter(Boolean)
            .join(" • ")
        : "";

      setActionMessage(
        `✗ Error saving scoring: ${err.message}${validationPreview ? ` (${validationPreview})` : ""}`
      );
    } finally {
      setScoringLoading(false);
    }
  };

  // Ottiene il colore dello status
  const getStatusColor = (status) => {
    const colors = {
      pending: "#f39c12",
      approved: "#27ae60",
      rejected: "#e74c3c",
      need_info: "#3498db",
      certified: "#8b5cf6",
      revoked: "#dc2626",
    };
    return colors[status] || "#95a5a6";
  };

  // Ottiene la label leggibile dello status
  const getStatusLabel = (status) => {
    const labels = {
      pending: "PENDING",
      approved: "APPROVED",
      rejected: "REJECTED",
      need_info: "NEED INFO",
      certified: "CERTIFIED",
      revoked: "REVOKED",
    };
    return labels[status] || status?.toUpperCase() || "UNKNOWN";
  };

  // Raggruppa le risposte per sezione usando la mappa centralizzata
  const groupAnswersBySection = (answers) => {
    // Arricchisci le risposte con metadati dalla mappa centralizzata
    const enriched = enrichAnswersWithMetadata(answers);
    
    // Raggruppa per sezione
    const sections = {
      E: { title: "Environment", answers: [] },
      S: { title: "Social", answers: [] },
      G: { title: "Governance", answers: [] },
      UNKNOWN: { title: "Unknown Questions", answers: [] }
    };
    
    enriched.forEach(item => {
      const section = sections[item.section] || sections.UNKNOWN;
      section.answers.push({
        questionKey: item.questionKey,
        code: item.visualNumber,
        label: item.title,
        answer: item.answer,
        description: item.description || item.evidence || "",
        inputType: item.inputType || "text"
      });
    });
    
    // Rimuovi sezioni vuote
    Object.keys(sections).forEach(key => {
      if (sections[key].answers.length === 0) {
        delete sections[key];
      }
    });
    
    return sections;
  };

  // Formatta la risposta in base al tipo
  const formatAnswer = (answer, inputType) => {
    if (answer === null || answer === undefined) return "-";
    if (inputType === "checkbox" || inputType === "boolean") {
      return answer ? "Yes" : "No";
    }
    return String(answer);
  };

  // Ottiene il badge per le risposte
  const getAnswerBadge = (answer, inputType) => {
    if (answer === null || answer === undefined) return null;
    
    const answerStr = String(answer).toLowerCase();
    
    // Badge colorati per risposte specifiche
    if (answerStr === "yes" || answerStr === "true") {
      return { text: "Yes", color: "#27ae60" }; // Verde
    }
    if (answerStr === "no" || answerStr === "false") {
      return { text: "No", color: "#e74c3c" }; // Rosso
    }
    if (answerStr === "partial" || answerStr === "in progress") {
      return { text: answer, color: "#f39c12" }; // Arancione
    }
    
    // Se è un numero, badge blu
    if (!isNaN(answer) && answer !== "") {
      return { text: String(answer), color: "#3498db" }; // Blu
    }
    
    return null; // Nessun badge per testo libero
  };

  // Formatta il file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  // Ottiene l'icona per il tipo di file
  const getFileIcon = (mimeType) => {
    if (!mimeType) return "📎";
    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("word") || mimeType.includes("document")) return "📝";
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "📊";
    if (mimeType.includes("image") || mimeType.includes("jpeg") || mimeType.includes("png")) return "🖼️";
    return "📎";
  };

  // Abbrevia wallet address
  const abbreviateWallet = (address) => {
    if (!address) return "N/A";
    if (address.length < 10) return address;
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Ottiene il colore per il tipo di azione
  const getActionColor = (action) => {
    const colors = {
      COMMENT: "#3498db",
      APPROVE: "#27ae60",
      REJECT: "#e74c3c",
      REQUEST_INFO: "#f39c12",
    };
    return colors[action] || "#95a5a6";
  };

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Error: {error}</div>
        <button onClick={() => navigate("/admin/esg")} style={styles.backButton}>
          ← Back to List
        </button>
      </div>
    );
  }

  if (!request) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Request not found</div>
        <button onClick={() => navigate("/admin/esg")} style={styles.backButton}>
          ← Back to List
        </button>
      </div>
    );
  }

  const sections = groupAnswersBySection(request.answers);
  const attachments = request.attachments || [];
  const adminActions = request.adminActions || [];
  const certificationData = publicCertification || request.certification || {};
  const certificationLevel =
    certificationData.level || request.certificationLevel || request.level || "NONE";
  const certificationStatus =
    certificationData.status || request.certificationStatus || request.status || "PENDING";
  const certificationBadges = certificationData.badges || request.badges || [];
  const certificationEvidence = certificationData.evidence || request.evidence || [];
  const issuedAt = certificationData.issuedAt || request.issuedAt;
  const expiresAt = certificationData.expiresAt || request.expiresAt;
  // Usa le helper functions per estrarre i CID da multiple sorgenti
  // Prova prima certificationData (from public API), poi request.certification (from admin API)
  const ipfsJsonCid = extractJsonCid(certificationData) || extractJsonCid(request);
  const ipfsPdfCid = extractPdfCid(certificationData) || extractPdfCid(request);
  const lastPublishedAt = extractLastPublishedAt(certificationData) || extractLastPublishedAt(request);
  
  // Debug: Log per capire perché i dati IPFS non ci sono
  console.log("🔍 ========= CERTIFICATE DATA DEBUG =========");
  console.log("📦 certificationData (from public API):", certificationData);
  console.log("📦 request.certification (from admin API):", request.certification);
  console.log("📦 Resolved certificationId:", resolveCertificationId(request));
  console.log("📦 Extracted ipfsJsonCid:", ipfsJsonCid);
  console.log("📦 Extracted ipfsPdfCid:", ipfsPdfCid);
  console.log("📦 Extracted lastPublishedAt:", lastPublishedAt);
  
  // Gateway URLs
  const jsonUrl = extractJsonUrl(certificationData) || extractJsonUrl(request);
  const pdfUrl = extractPdfUrl(certificationData) || extractPdfUrl(request);
  console.log("📦 Extracted jsonUrl:", jsonUrl);
  console.log("📦 Extracted pdfUrl:", pdfUrl);
  
  console.log("📦 certificationData keys:", certificationData ? Object.keys(certificationData) : 'null');
  console.log("📦 request.certification keys:", request?.certification ? Object.keys(request.certification) : 'null');
  console.log("📦 request keys with 'ipfs':", request ? Object.keys(request).filter(k => k.toLowerCase().includes('ipfs')) : 'none');
  console.log("📦 All request keys:", request ? Object.keys(request) : 'null');
  console.log("🔍 =========================================");
  
  // Normalizza campi con retrocompatibilità
  const contactEmail = request.contactEmail || request.email || request.contact_email;
  const submittedAt = request.submittedAt || request.createdAt || request.submitted_at || request.created_at;
  
  // Estrai logo SOLO da attachments (fonte affidabile per ESG requests)
  const logoAttachment = attachments.find(att => 
    (att.question_key || att.questionKey) === 'q11_company_logo'
  );
  
  // Usa SOLO attachment - ignora request.logoUrl che potrebbe essere logo admin/app
  const finalLogoUrl = logoAttachment?.presignedUrl || logoAttachment?.url || null;
  
  console.log("📊 Sections grouped:", sections);
  console.log("📊 Number of sections:", Object.keys(sections).length);
  console.log("🏢 ========= LOGO DEBUG =========");
  console.log("🏢 request.logoUrl (IGNORATO):", request.logoUrl);
  console.log("🏢 request.company?.logo (IGNORATO):", request.company?.logo);
  console.log("🏢 All attachments:", attachments);
  console.log("🏢 Logo attachment found:", logoAttachment);
  console.log("🏢 logoAttachment.presignedUrl:", logoAttachment?.presignedUrl);
  console.log("🏢 logoAttachment.url:", logoAttachment?.url);
  console.log("🏢 Final Logo URL (DA ATTACHMENT):", finalLogoUrl);
  console.log("🏢 ==============================");

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate("/admin/esg")} style={styles.backButton}>
          ← Back to List
        </button>
        <h2 style={styles.title}>ESG Request Detail</h2>
      </div>

      {/* Message di azione */}
      {actionMessage && (
        <div
          style={{
            ...styles.actionMessage,
            backgroundColor: actionMessage.startsWith("✓") ? "#d4edda" : "#f8d7da",
            color: actionMessage.startsWith("✓") ? "#155724" : "#721c24",
          }}
        >
          {actionMessage}
        </div>
      )}

      {/* Company Header con Logo */}
      <div style={styles.companyHeader}>
        <div style={styles.logoContainer}>
          {finalLogoUrl ? (
            <img 
              src={finalLogoUrl} 
              alt="Company Logo" 
              style={styles.companyLogoThumbnail}
              onError={async (e) => { 
                console.warn("Logo failed to load, URL might be expired");
                
                // Retry una sola volta ri-fetchando i dati
                if (!logoRetryAttempted) {
                  setLogoRetryAttempted(true);
                  console.log("Retrying request detail fetch for fresh presigned URL...");
                  try {
                    await loadRequest();
                  } catch (err) {
                    console.error("Retry fetch failed:", err);
                    // Mostra placeholder
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.logo-placeholder');
                    if (placeholder) placeholder.style.display = 'flex';
                  }
                } else {
                  // Secondo fallimento, mostra placeholder
                  e.target.style.display = 'none';
                  const placeholder = e.target.parentElement.querySelector('.logo-placeholder');
                  if (placeholder) placeholder.style.display = 'flex';
                }
              }}
            />
          ) : null}
          <div 
            className="logo-placeholder"
            style={{
              ...styles.logoPlaceholder,
              display: finalLogoUrl ? 'none' : 'flex'
            }}
          >
            {request.companyName?.substring(0, 2).toUpperCase() || '??'}
          </div>
        </div>
        <div style={styles.companyInfo}>
          <h2 style={styles.companyName}>{request.companyName}</h2>
          <div style={styles.companyMeta}>
            <span style={styles.companyMetaItem}>
              📧 {contactEmail}
            </span>
              {request.walletAddress && (
                <span style={styles.companyMetaItem}>
                  🔗 {request.walletAddress.substring(0, 8)}...{request.walletAddress.substring(request.walletAddress.length - 6)}
                </span>
              )}
              <span style={styles.companyMetaItem}>
                📅 {formatEnglishDate(submittedAt)}
              </span>
            </div>
          </div>
        </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            ...styles.tabButton,
            ...(activeTab === "overview" ? styles.tabButtonActive : {}),
          }}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("scores")}
          style={{
            ...styles.tabButton,
            ...(activeTab === "scores" ? styles.tabButtonActive : {}),
          }}
        >
          Score Dettagliati
        </button>
        <button
          onClick={() => setActiveTab("ipfs")}
          style={{
            ...styles.tabButton,
            ...(activeTab === "ipfs" ? styles.tabButtonActive : {}),
          }}
        >
          IPFS & Blockchain
        </button>
      </div>

      {activeTab === "overview" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>🎖️ Certificazione</h3>
            <span
              style={{
                ...styles.statusBadge,
                backgroundColor: getStatusColor(certificationStatus?.toLowerCase?.() || certificationStatus),
              }}
            >
              {certificationStatus || "PENDING"}
            </span>
          </div>

          {publicCertificationLoading && <div style={styles.loading}>Caricamento certificazione...</div>}
          {publicCertificationError && <div style={styles.error}>{publicCertificationError}</div>}

          <div style={styles.certOverview}>
            <LevelBadge level={certificationLevel} size="large" showLabel />
            <div style={styles.certMeta}>
              <div style={styles.certMetaRow}>
                <strong>Validità:</strong> {formatEnglishDate(issuedAt)} - {formatEnglishDate(expiresAt)}
              </div>
              <div style={styles.certMetaRow}>
                <strong>Badge ottenuti:</strong> {certificationBadges.length}
              </div>
              <div style={styles.certMetaRow}>
                <strong>Evidenze:</strong> {certificationEvidence.length}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <h4 style={styles.sectionSubtitle}>Badge ottenuti</h4>
            <BadgesGrid badges={certificationBadges} showDescription />
          </div>

          <div style={{ marginTop: 24 }}>
            <h4 style={styles.sectionSubtitle}>Evidenze verificate</h4>
            <EvidenceList evidence={certificationEvidence} variant="detailed" />
          </div>
        </div>
      )}

      {/* Card Dati Azienda */}
      {activeTab === "overview" && (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>🏢 Dati Azienda</h3>
          <span
            style={{
              ...styles.statusBadge,
              backgroundColor: getStatusColor(request.status),
            }}
          >
            {getStatusLabel(request.status)}
          </span>
        </div>

        <div style={styles.companyInfoGrid}>
          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>🏢</div>
            <div>
              <div style={styles.companyInfoLabel}>Company Name</div>
              <div style={styles.companyInfoValue}>{request.companyName || "-"}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>✉️</div>
            <div>
              <div style={styles.companyInfoLabel}>Email</div>
              <div style={styles.companyInfoValue}>{request.email || "-"}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>🆔</div>
            <div>
              <div style={styles.companyInfoLabel}>VAT Number</div>
              <div style={styles.companyInfoValue}>{request.vatNumber || "-"}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>📞</div>
            <div>
              <div style={styles.companyInfoLabel}>Phone</div>
              <div style={styles.companyInfoValue}>{request.phone || "-"}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>📍</div>
            <div>
              <div style={styles.companyInfoLabel}>Address</div>
              <div style={styles.companyInfoValue}>{request.address || "-"}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>�</div>
            <div>
              <div style={styles.companyInfoLabel}>Wallet Address</div>
              <div style={{...styles.companyInfoValue, fontSize: '13px', fontFamily: 'monospace'}}>
                {request.walletAddress || "-"}
              </div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>�📅</div>
            <div>
              <div style={styles.companyInfoLabel}>Created At</div>
              <div style={styles.companyInfoValue}>{formatEnglishDate(request.createdAt)}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>🔄</div>
            <div>
              <div style={styles.companyInfoLabel}>Last Updated</div>
              <div style={styles.companyInfoValue}>{formatEnglishDate(request.updatedAt)}</div>
            </div>
          </div>

          <div style={styles.companyInfoItem}>
            <div style={styles.companyInfoIcon}>🔑</div>
            <div>
              <div style={styles.companyInfoLabel}>Request ID</div>
              <div style={styles.companyInfoValue}>{request.id}</div>
            </div>
          </div>
        </div>
      </div>
      )}

      {activeTab === "scores" && (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>📋 Risposte Questionario ESG</h3>
          <button
            onClick={handleSaveScoring}
            disabled={scoringLoading}
            style={{
              ...styles.saveScoringButton,
              opacity: scoringLoading ? 0.6 : 1,
            }}
          >
            {scoringLoading ? "Saving..." : "💾 Save Scoring"}
          </button>
        </div>

        {scoringTotals && (
          <div style={styles.scoringTotals}>
            <h4 style={styles.totalsTitle}>📊 Scoring Totals:</h4>
            <div style={styles.totalsGrid}>
              <div style={styles.totalItem}>
                <span style={styles.totalLabel}>Environment (E):</span>
                <span style={styles.totalValue}>{scoringTotals.E || 0} / 40</span>
              </div>
              <div style={styles.totalItem}>
                <span style={styles.totalLabel}>Social (S):</span>
                <span style={styles.totalValue}>{scoringTotals.S || 0} / 35</span>
              </div>
              <div style={styles.totalItem}>
                <span style={styles.totalLabel}>Governance (G):</span>
                <span style={styles.totalValue}>{scoringTotals.G || 0} / 25</span>
              </div>
              <div style={styles.totalItem}>
                <span style={styles.totalLabel}>TOTAL:</span>
                <span style={{...styles.totalValue, fontWeight: "800", fontSize: "18px"}}>
                  {scoringTotals.total || 0} / 100
                </span>
              </div>
            </div>
          </div>
        )}

        {Object.keys(sections).length === 0 ? (
          <div style={styles.noDataMessage}>
            Nessuna risposta trovata. Controlla la console per dettagli.
          </div>
        ) : (
          Object.entries(sections).map(([sectionKey, section]) => (
          <div key={sectionKey} style={styles.section}>
            <h4 style={styles.sectionTitle}>{section.title}</h4>
            
            {/* Tabella risposte */}
            <div style={styles.answersTable}>
              {/* Header */}
              <div style={styles.answersTableHeader}>
                <div style={styles.answersTableHeaderCell}>Cod.</div>
                <div style={styles.answersTableHeaderCell}>Domanda</div>
                <div style={styles.answersTableHeaderCell}>Risposta</div>
                <div style={styles.answersTableHeaderCell}>Description / Evidence</div>
                <div style={styles.answersTableHeaderCell}>File</div>
                <div style={styles.answersTableHeaderCell}>Max Points</div>
                <div style={styles.answersTableHeaderCell}>Score</div>
                <div style={styles.answersTableHeaderCell}>Evidence ✓</div>
                <div style={styles.answersTableHeaderCell}>Notes</div>
              </div>
              
              {/* Righe */}
              {section.answers.map((item) => {
                const badge = getAnswerBadge(item.answer, item.type);
                const relatedAttachments = attachments.filter(
                  att => (att.question_key || att.questionKey) === item.questionKey
                );
                const maxPoints = getMaxPoints(item.questionKey);
                const questionScoring = scoring[item.questionKey] || { score: 0, evidenceVerified: false, notes: "" };
                
                return (
                  <div key={item.questionKey} style={styles.answersTableRow}>
                    {/* Codice */}
                    <div style={styles.answersTableCell}>
                      <span style={styles.questionCode}>{item.code}</span>
                    </div>
                    
                    {/* Domanda */}
                    <div style={styles.answersTableCell}>
                      <span style={styles.questionLabel}>{item.label}</span>
                    </div>
                    
                    {/* Risposta */}
                    <div style={styles.answersTableCell}>
                      {badge ? (
                        <span
                          style={{
                            ...styles.answerBadge,
                            backgroundColor: badge.color,
                          }}
                        >
                          {badge.text}
                        </span>
                      ) : (
                        <span style={styles.answerValue}>
                          {formatAnswer(item.answer, item.type)}
                        </span>
                      )}
                    </div>
                    
                    {/* Description/Evidence */}
                    <div style={styles.answersTableCell}>
                      {item.description ? (
                        <div style={styles.descriptionText}>
                          {item.description}
                        </div>
                      ) : (
                        <span style={styles.emptyCell}>-</span>
                      )}
                    </div>
                    
                    {/* File */}
                    <div style={styles.answersTableCell}>
                      {relatedAttachments.length > 0 ? (
                        <div style={styles.filesList}>
                          {relatedAttachments.map((att) => {
                            const icon = getFileIcon(att.mime_type || att.mimeType);
                            const fileName = att.file_name || att.fileName;
                            return (
                              <div key={att.id} style={styles.fileItem}>
                                <span style={styles.fileIcon}>{icon}</span>
                                <button
                                  onClick={() => handleDownloadAttachment(att.id, fileName)}
                                  style={styles.fileDownloadBtn}
                                  title={fileName}
                                >
                                  {fileName.length > 20 ? fileName.substring(0, 17) + '...' : fileName}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={styles.emptyCell}>-</span>
                      )}
                    </div>
                    
                    {/* Max Points */}
                    <div style={styles.answersTableCell}>
                      <span style={styles.maxPointsBadge}>
                        {getMaxPoints(item.questionKey)} pts
                      </span>
                    </div>
                    
                    {/* Score Input */}
                    <div style={styles.answersTableCell}>
                      <input
                        type="number"
                        min="0"
                        max={maxPoints}
                        step="1"
                        value={questionScoring.score || 0}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val >= 0 && val <= maxPoints) {
                            handleScoreChange(item.questionKey, 'score', val);
                          }
                        }}
                        style={styles.scoreInput}
                      />
                    </div>
                    
                    {/* Evidence Verified Checkbox */}
                    <div style={styles.answersTableCell}>
                      <input
                        type="checkbox"
                        checked={questionScoring.evidenceVerified || false}
                        onChange={(e) => handleScoreChange(item.questionKey, 'evidenceVerified', e.target.checked)}
                        style={styles.evidenceCheckbox}
                      />
                    </div>
                    
                    {/* Notes Input */}
                    <div style={styles.answersTableCell}>
                      <input
                        type="text"
                        value={questionScoring.notes || ""}
                        onChange={(e) => handleScoreChange(item.questionKey, 'notes', e.target.value)}
                        placeholder="Optional notes..."
                        style={styles.notesInput}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
        )}

        <div style={styles.scoreHistory}>
          <h4 style={styles.sectionSubtitle}>Storico modifiche score</h4>
          {request.scoringHistory && request.scoringHistory.length > 0 ? (
            <ul style={styles.historyList}>
              {request.scoringHistory.map((item, index) => (
                <li key={`${item.updatedAt || index}`} style={styles.historyItem}>
                  {formatEnglishDate(item.updatedAt)} - {item.note || "Aggiornamento scoring"}
                </li>
              ))}
            </ul>
          ) : (
            <div style={styles.emptyHistory}>Nessuno storico disponibile</div>
          )}
        </div>
      </div>
      )}

      {activeTab === "ipfs" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>🌐 IPFS & Blockchain</h3>
          </div>

          <IPFSLinks 
            jsonCid={ipfsJsonCid} 
            pdfCid={ipfsPdfCid}
            jsonUrl={extractJsonUrl(certificationData) || extractJsonUrl(request)}
            pdfUrl={extractPdfUrl(certificationData) || extractPdfUrl(request)}
            variant="buttons" 
          />

          <div style={{ marginTop: 16 }}>
            <CertificationQRCode certificationId={certificationData?.certificationId || request.certificationId || id} size={160} />
          </div>

          <div style={styles.ipfsMetaGrid}>
            <div>
              <div style={styles.ipfsMetaLabel}>CID JSON</div>
              <div style={styles.ipfsMetaValue}>{ipfsJsonCid || "-"}</div>
            </div>
            <div>
              <div style={styles.ipfsMetaLabel}>CID PDF</div>
              <div style={styles.ipfsMetaValue}>{ipfsPdfCid || "-"}</div>
            </div>
            <div>
              <div style={styles.ipfsMetaLabel}>Pubblicato il</div>
              <div style={styles.ipfsMetaValue}>{formatEnglishDate(lastPublishedAt)}</div>
            </div>
          </div>

          <div style={styles.scoreHistory}>
            <h4 style={styles.sectionSubtitle}>Storico pubblicazioni</h4>
            {request.ipfsHistory && request.ipfsHistory.length > 0 ? (
              <ul style={styles.historyList}>
                {request.ipfsHistory.map((item, index) => (
                  <li key={`${item.publishedAt || index}`} style={styles.historyItem}>
                    {formatEnglishDate(item.publishedAt)} - {item.cid || "Pubblicazione"}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={styles.emptyHistory}>Nessuna pubblicazione registrata</div>
            )}
          </div>
        </div>
      )}

      {/* Allegati */}
      {activeTab === "overview" && attachments.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>📎 Allegati ({attachments.length})</h3>
            <button onClick={handleDownloadZip} style={styles.downloadZipButton}>
              ⬇ Download All (ZIP)
            </button>
          </div>

          <div style={styles.attachmentsList}>
            {attachments.map((att) => {
              const question = QUESTIONS_BY_ID.get(att.question_key || att.questionKey);
              const icon = getFileIcon(att.mime_type || att.mimeType);
              const size = formatFileSize(att.size);
              const fileName = att.file_name || att.fileName;
              
              return (
                <div key={att.id} style={styles.attachmentItem}>
                  <div style={styles.attachmentIconContainer}>
                    <span style={styles.attachmentIcon}>{icon}</span>
                  </div>
                  <div style={styles.attachmentInfo}>
                    <div style={styles.attachmentFileName}>{fileName}</div>
                    <div style={styles.attachmentMeta}>
                      {question ? `${question.visualNumber} - ${question.title}` : (att.question_key || att.questionKey)} • {size}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadAttachment(att.id, fileName)}
                    style={styles.downloadButton}
                  >
                    ⬇ Download
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KYC Status */}
      {activeTab === "overview" && request.kyc && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🔐 KYC Verification</h3>
          <div style={styles.kycContainer}>
            <div style={styles.kycStatusRow}>
              <span style={styles.kycLabel}>Status:</span>
              <span 
                style={{
                  ...styles.kycStatusBadge,
                  backgroundColor: 
                    request.kyc.status === 'VERIFIED' ? '#27ae60' :
                    request.kyc.status === 'PENDING' ? '#f39c12' :
                    request.kyc.status === 'REJECTED' ? '#e74c3c' :
                    '#e67e22', // EXPIRED
                }}
              >
                {request.kyc.status === 'VERIFIED' && '✓ '}
                {request.kyc.status === 'PENDING' && '⏳ '}
                {request.kyc.status === 'REJECTED' && '✗ '}
                {request.kyc.status === 'EXPIRED' && '⚠️ '}
                {request.kyc.status}
              </span>
            </div>
            <div style={styles.kycInfoGrid}>
              <div style={styles.kycInfoItem}>
                <span style={styles.kycInfoLabel}>Document Number:</span>
                <span style={styles.kycInfoValue}>{request.kyc.documentNumber}</span>
              </div>
              <div style={styles.kycInfoItem}>
                <span style={styles.kycInfoLabel}>Nationality:</span>
                <span style={styles.kycInfoValue}>{request.kyc.nationality}</span>
              </div>
            </div>
            {request.kyc.verifiedAt && (
              <div style={styles.kycTimestamp}>
                Verified: {formatEnglishDate(request.kyc.verifiedAt)}
              </div>
            )}
            {request.kyc.providerReference && (
              <div style={styles.kycReference}>
                Provider Ref: <span style={styles.kycReferenceCode}>{request.kyc.providerReference}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Status */}
      {activeTab === "overview" && request.paymentInfo && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>💳 Certification Payment</h3>
          
          {/* Quote Section */}
          <div style={styles.paymentSection}>
            <p style={styles.paymentSectionTitle}>Payment Quote</p>
            <div style={styles.paymentQuoteHeader}>
              <div>
                <span style={styles.paymentAmount}>
                  {request.paymentInfo.quote.amount} {request.paymentInfo.quote.currency}
                </span>
                {request.paymentInfo.quote.isExpired && (
                  <span style={styles.expiredBadge}>⚠️ EXPIRED</span>
                )}
              </div>
            </div>
            <div style={styles.paymentInfoRow}>
              <span style={styles.paymentInfoLabel}>Valid until:</span>
              <span style={styles.paymentInfoValue}>
                {formatEnglishDate(request.paymentInfo.quote.validUntil)}
              </span>
            </div>
            <div style={styles.paymentAddressRow}>
              <span style={styles.paymentInfoLabel}>Payment Address:</span>
              <span style={styles.paymentAddress}>
                {request.paymentInfo.quote.paymentAddress.substring(0, 10)}...
                {request.paymentInfo.quote.paymentAddress.substring(38)}
              </span>
            </div>
          </div>

          {/* Payment Received Section */}
          {request.paymentInfo.payment && (
            <div style={styles.paymentSection}>
              <p style={styles.paymentSectionTitle}>Payment Received</p>
              <div style={styles.paymentStatusRow}>
                <span style={styles.paymentInfoLabel}>Status:</span>
                <span 
                  style={{
                    ...styles.paymentStatusBadge,
                    backgroundColor: 
                      request.paymentInfo.payment.status === 'PAID' ? '#27ae60' :
                      request.paymentInfo.payment.status === 'CONFIRMED' ? '#3498db' :
                      '#f39c12', // PENDING
                  }}
                >
                  {request.paymentInfo.payment.status === 'PAID' && '✓ '}
                  {request.paymentInfo.payment.status}
                </span>
              </div>
              
              {/* Confirmations Progress */}
              <div style={styles.confirmationsContainer}>
                <div style={styles.confirmationsHeader}>
                  <span style={styles.confirmationsLabel}>Confirmations</span>
                  <span style={styles.confirmationsCount}>
                    {request.paymentInfo.payment.confirmations}/20
                  </span>
                </div>
                <div style={styles.progressBarContainer}>
                  <div 
                    style={{
                      ...styles.progressBar,
                      width: `${Math.min(request.paymentInfo.payment.confirmations / 20 * 100, 100)}%`,
                      backgroundColor: request.paymentInfo.payment.confirmations >= 20 ? '#27ae60' : '#3498db',
                    }}
                  />
                </div>
              </div>

              {/* Transaction Link */}
              <div style={styles.paymentInfoRow}>
                <span style={styles.paymentInfoLabel}>Transaction:</span>
                <a 
                  href={request.paymentInfo.payment.explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.txLink}
                >
                  {request.paymentInfo.payment.txHash.substring(0, 10)}...
                  {request.paymentInfo.payment.txHash.substring(58)}
                  <span style={styles.txLinkIcon}> 🔗</span>
                </a>
              </div>

              {request.paymentInfo.payment.verifiedAt && (
                <div style={styles.paymentTimestamp}>
                  Verified: {formatEnglishDate(request.paymentInfo.payment.verifiedAt)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Certification Status */}
        {activeTab === "overview" && request.certification && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📜 ESG Certification</h3>
          <div style={styles.certificationContainer}>
            <div style={styles.certificationStatusRow}>
              <span style={styles.certificationLabel}>Status:</span>
              <span 
                style={{
                  ...styles.certificationStatusBadge,
                  backgroundColor: 
                    request.certification.status === 'CERTIFIED' ? '#8b5cf6' :
                    request.certification.status === 'PENDING_PAYMENT' ? '#f39c12' :
                    '#95a5a6', // PENDING_KYC
                }}
              >
                {request.certification.status === 'CERTIFIED' && '✓ '}
                {request.certification.status.replace(/_/g, ' ')}
              </span>
            </div>

            {/* Download Buttons */}
            {request.certification.status === 'CERTIFIED' && (
              <div style={styles.certificationButtons}>
                {request.certification.finalPdfUrl && (
                  <a
                    href={request.certification.finalPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.certificationDownloadBtn}
                  >
                    📄 Download PDF
                  </a>
                )}
                {request.certification.finalIpfsHash && (
                  <a
                    href={`https://gateway.pinata.cloud/ipfs/${request.certification.finalIpfsHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.certificationIpfsBtn}
                  >
                    🌐 View on IPFS
                  </a>
                )}
              </div>
            )}

            {/* Certification Info Grid */}
            <div style={styles.certificationInfoGrid}>
              <div style={styles.certificationInfoItem}>
                <span style={styles.certificationInfoLabel}>KYC Status:</span>
                <span style={styles.certificationInfoValue}>{request.certification.kycStatus}</span>
              </div>
              <div style={styles.certificationInfoItem}>
                <span style={styles.certificationInfoLabel}>Payment Status:</span>
                <span style={styles.certificationInfoValue}>{request.certification.paymentStatus}</span>
              </div>
            </div>

            {request.certification.certificationDate && (
              <div style={styles.certificationTimestamp}>
                Issued: {formatEnglishDate(request.certification.certificationDate)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commenti Admin */}
        {activeTab === "overview" && adminActions.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>💬 Storico Azioni Admin</h3>
          <div style={styles.timeline}>
            {adminActions.map((action) => (
              <div key={action.id} style={styles.timelineItem}>
                <div style={styles.timelineDot}></div>
                <div style={styles.timelineContent}>
                  <div style={styles.timelineHeader}>
                    <span
                      style={{
                        ...styles.actionTypeBadge,
                        backgroundColor: getActionColor(action.action),
                      }}
                    >
                      {action.action}
                    </span>
                    <span style={styles.timelineDate}>
                      {formatEnglishDate(action.createdAt)}
                    </span>
                  </div>
                  <div style={styles.timelineUser}>
                    👤 Admin: {abbreviateWallet(action.adminUser)}
                  </div>
                  {action.note && (
                    <div style={styles.timelineNote}>{action.note}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form per Nuovo Commento */}
        {activeTab === "overview" && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>➕ Aggiungi Commento</h3>
            <div style={styles.commentFormContainer}>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Scrivi un commento o annotazione..."
                style={styles.commentTextarea}
                rows="4"
              />
              <button
                onClick={handleAddComment}
                disabled={commentLoading || !commentText.trim()}
                style={{
                  ...styles.commentButton,
                  opacity: commentLoading || !commentText.trim() ? 0.6 : 1,
                  cursor: commentLoading || !commentText.trim() ? "not-allowed" : "pointer",
                }}
              >
                {commentLoading ? "Invio..." : "💬 Aggiungi Commento"}
              </button>
            </div>
          </div>
        )}

      {/* Azioni */}
      {activeTab === "overview" && (
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>⚡ Azioni</h3>

        {/* Azioni Status Rapido */}
        <div style={styles.quickActionsContainer}>
          <label style={styles.quickActionsLabel}>Cambia Stato:</label>
          <div style={styles.quickActionsButtons}>
            <button
              onClick={() => handleUpdateStatus("pending")}
              disabled={actionLoading}
              style={{
                ...styles.quickActionButton,
                backgroundColor: "#f39c12",
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Pending
            </button>
            <button
              onClick={() => handleUpdateStatus("approved")}
              disabled={actionLoading}
              style={{
                ...styles.quickActionButton,
                backgroundColor: "#27ae60",
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Approved
            </button>
            <button
              onClick={() => handleUpdateStatus("rejected")}
              disabled={actionLoading}
              style={{
                ...styles.quickActionButton,
                backgroundColor: "#e74c3c",
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Rejected
            </button>
            <button
              onClick={() => handleUpdateStatus("need_info")}
              disabled={actionLoading}
              style={{
                ...styles.quickActionButton,
                backgroundColor: "#3498db",
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Need Info
            </button>
          </div>
        </div>

        {/* Azioni Standard (mantenute per compatibilità) */}
        {(request.status === "pending" || request.status === "need_info" || request.status === "PENDING") && (
          <>
            <div style={styles.notesContainer}>
              <label style={styles.notesLabel}>
                Motivo/Messaggio (OBBLIGATORIO per Reject e Need Info):
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Per Reject: specifica il motivo del rifiuto&#10;Per Need Info: indica quali informazioni aggiuntive sono richieste&#10;Per Approve: opzionale"
                style={styles.notesTextarea}
                rows="4"
              />
            </div>

            <div style={styles.actionsButtons}>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                style={{
                  ...styles.actionButton,
                  backgroundColor: "#27ae60",
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                ✓ Approve
              </button>

              <button
                onClick={handleRequestMoreInfo}
                disabled={actionLoading}
                style={{
                  ...styles.actionButton,
                  backgroundColor: "#3498db",
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                ℹ Request More Info
              </button>

              <button
                onClick={handleReject}
                disabled={actionLoading}
                style={{
                  ...styles.actionButton,
                  backgroundColor: "#e74c3c",
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                ✗ Reject
              </button>
            </div>
          </>
        )}

        {/* Pulsante Certificazione On-Chain */}
        {request.status === "approved" || request.status === "APPROVED" ? (
          <div style={styles.certifySection}>
            <h4 style={styles.certifySectionTitle}>🔐 Certification Options</h4>
            <p style={styles.certifySectionDesc}>
              This request has been approved. Choose certification method:
            </p>
            
            <div style={styles.certificationMethodsGrid}>
              {/* On-Chain Certification (con wallet) */}
              <div style={styles.certificationMethod}>
                <h5 style={styles.methodTitle}>🔗 On-Chain (Blockchain)</h5>
                <p style={styles.methodDescription}>
                  For companies with wallet. Certificate will be recorded on blockchain.
                  Requires signing transaction.
                </p>
                <AdminCertifyButton
                  requestId={id}
                  companyWalletAddress={request.walletAddress || request.wallet_address}
                  adminKey={adminKey}
                  walletAddress={walletAddress}
                  onSuccess={(data) => {
                    // data.certification contiene: certificationId, txHash, confirmedAt, expiresAt, blockchainExplorerUrl
                    const certification = data.certification || {};
                    const certData = data.certificationData || {};
                    const txHash = certification.txHash || data.txHash;
                    const explorerUrl = certification.blockchainExplorerUrl || `https://arbiscan.io/tx/${txHash}`;
                    
                    setActionMessage(
                      `✓ Certified successfully! ` +
                      `Certification ID: ${certification.certificationId || 'N/A'} | ` +
                      `TX: ${txHash?.substring(0, 12)}...`
                    );
                    
                    // Update request with certification data
                    setRequest(prev => ({
                      ...prev,
                      certificationTxHash: txHash,
                      certificationTxLink: explorerUrl,
                      certificationId: certification.certificationId,
                      reportCID: certData.ipfsHash,
                      status: 'certified',
                      confirmedAt: certification.confirmedAt,
                      expiresAt: certification.expiresAt
                    }));
                    loadRequest();
                  }}
                  onError={(error) => {
                    setActionMessage(`✗ Certification failed: ${error}`);
                  }}
                />
              </div>

              {/* Off-Chain Certification (senza wallet) */}
              <div style={styles.certificationMethod}>
                <h5 style={styles.methodTitle}>📋 Off-Chain (Database Only)</h5>
                <p style={styles.methodDescription}>
                  For companies without wallet or internal certifications.
                  Certificate saved in database only (no blockchain).
                </p>
                <AdminCertifyOffChainButton
                  requestId={id}
                  adminKey={adminKey}
                  walletAddress={walletAddress}
                  onSuccess={(data) => {
                    setActionMessage(`✓ Off-Chain certification completed successfully!`);
                    loadRequest();
                  }}
                  onError={(error) => {
                    setActionMessage(`✗ Off-Chain certification failed: ${error}`);
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}

        {/* Pulsante Revoca Certificazione */}
        {request.status === "certified" || request.status === "CERTIFIED" ? (
          <div style={styles.decertifySection}>
            <h4 style={styles.decertifySectionTitle}>⚠️ Revoke Certification</h4>
            <p style={styles.decertifySectionDesc}>
              This company is currently certified. You can revoke the certification on-chain.
            </p>
            <AdminRevokeButton
              requestId={id}
              companyWalletAddress={request.walletAddress || request.wallet_address}
              adminKey={adminKey}
              walletAddress={walletAddress}
              onSuccess={(data) => {
                // data contiene: certificationStatus, revokedTxHash, revokedAt, blockchainExplorerUrl
                const txHash = data.revokedTxHash;
                const explorerUrl = data.blockchainExplorerUrl || `https://arbiscan.io/tx/${txHash}`;
                
                setActionMessage(
                  `✓ Certification revoked successfully! ` +
                  `TX: ${txHash?.substring(0, 12)}...`
                );
                
                // Update status to revoked
                setRequest(prev => ({
                  ...prev,
                  revocationTxHash: txHash,
                  revocationTxLink: explorerUrl,
                  status: 'revoked',
                  certificationStatus: data.certificationStatus,
                  revokedAt: data.revokedAt
                }));
                loadRequest();
              }}
              onError={(error) => {
                setActionMessage(`✗ Revocation failed: ${error}`);
              }}
            />
          </div>
        ) : null}

        {/* Pulsante Elimina */}
        <div style={styles.dangerZone}>
          <div style={styles.dangerZoneHeader}>
            <strong>⚠️ Zona Pericolosa</strong>
          </div>
          <button
            onClick={handleDeleteRequest}
            disabled={actionLoading}
            style={{
              ...styles.deleteRequestButton,
              opacity: actionLoading ? 0.6 : 1,
            }}
          >
            🗑️ Elimina Richiesta
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    fontSize: "16px",
    color: "#7f8c8d",
  },
  error: {
    padding: "20px",
    backgroundColor: "#f8d7da",
    color: "#721c24",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  header: {
    marginBottom: "20px",
  },
  backButton: {
    padding: "10px 20px",
    backgroundColor: "#95a5a6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "15px",
    transition: "background-color 0.2s",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#2c3e50",
    margin: 0,
  },
  card: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "25px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
  },
  cardTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2c3e50",
    margin: 0,
  },
  companyHeader: {
    backgroundColor: "#f8f9fa",
    padding: "24px",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    marginBottom: "24px",
    display: "flex",
    alignItems: "center",
    gap: "24px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  logoContainer: {
    flexShrink: 0,
  },
  companyLogoThumbnail: {
    maxWidth: "120px",
    maxHeight: "120px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "2px solid #e0e0e0",
    backgroundColor: "white",
    padding: "8px",
  },
  logoPlaceholder: {
    width: "120px",
    height: "120px",
    borderRadius: "8px",
    border: "2px solid #e0e0e0",
    backgroundColor: "#3498db",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "36px",
    fontWeight: "700",
    textTransform: "uppercase",
  },
  companyInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  companyName: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#2c3e50",
    margin: 0,
  },
  companyMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    fontSize: "14px",
    color: "#7f8c8d",
  },
  companyMetaItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  statusBadge: {
    display: "inline-block",
    padding: "8px 20px",
    borderRadius: "20px",
    color: "white",
    fontSize: "13px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  actionMessage: {
    padding: "15px 20px",
    borderRadius: "8px",
    marginBottom: "20px",
    fontSize: "15px",
    fontWeight: "600",
  },
  tabs: {
    display: "flex",
    gap: "12px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  tabButton: {
    padding: "10px 18px",
    borderRadius: "999px",
    border: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    fontWeight: "600",
    cursor: "pointer",
  },
  tabButtonActive: {
    backgroundColor: "#2563eb",
    color: "#ffffff",
    borderColor: "#2563eb",
  },
  certOverview: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap",
  },
  certMeta: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#334155",
  },
  certMetaRow: {
    fontSize: "14px",
  },
  sectionSubtitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "12px",
  },
  scoreHistory: {
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
  },
  historyList: {
    margin: 0,
    paddingLeft: "18px",
  },
  historyItem: {
    fontSize: "13px",
    color: "#475569",
    marginBottom: "6px",
  },
  emptyHistory: {
    fontSize: "13px",
    color: "#94a3b8",
  },
  ipfsMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginTop: "16px",
  },
  ipfsMetaLabel: {
    fontSize: "12px",
    textTransform: "uppercase",
    color: "#94a3b8",
    fontWeight: "700",
  },
  ipfsMetaValue: {
    fontSize: "13px",
    color: "#334155",
    wordBreak: "break-all",
  },
  // Stili per la sezione dati azienda
  companyInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  companyInfoItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "15px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
  companyInfoIcon: {
    fontSize: "28px",
    lineHeight: "1",
    marginTop: "2px",
  },
  companyInfoLabel: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "5px",
  },
  companyInfoValue: {
    fontSize: "15px",
    color: "#2c3e50",
    fontWeight: "500",
    wordBreak: "break-word",
  },
  // Stili per le sezioni delle risposte
  section: {
    marginBottom: "35px",
  },
  sectionTitle: {
    fontSize: "19px",
    fontWeight: "700",
    color: "#27ae60",
    marginBottom: "18px",
    paddingBottom: "10px",
    borderBottom: "3px solid #27ae60",
  },
  // Nuova tabella risposte
  answersTable: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e9ecef",
    borderRadius: "8px",
    overflow: "hidden",
  },
  answersTableHeader: {
    display: "grid",
    gridTemplateColumns: "70px 2fr 1.2fr 2fr 1.5fr 100px 90px 80px 1.5fr",
    gap: "10px",
    backgroundColor: "#34495e",
    padding: "12px 15px",
    fontWeight: "700",
    fontSize: "13px",
  },
  answersTableHeaderCell: {
    color: "white",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  answersTableRow: {
    display: "grid",
    gridTemplateColumns: "70px 2fr 1.2fr 2fr 1.5fr 100px 90px 80px 1.5fr",
    gap: "10px",
    padding: "12px 15px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e9ecef",
    alignItems: "start",
  },
  answersTableCell: {
    fontSize: "13px",
    color: "#2c3e50",
    wordBreak: "break-word",
  },
  questionCode: {
    display: "inline-block",
    padding: "4px 8px",
    backgroundColor: "#3498db",
    color: "white",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "700",
    fontFamily: "monospace",
  },
  questionLabel: {
    fontSize: "13px",
    color: "#495057",
    fontWeight: "600",
    lineHeight: "1.5",
  },
  answerValue: {
    fontSize: "13px",
    color: "#2c3e50",
    fontWeight: "500",
  },
  answerBadge: {
    display: "inline-block",
    padding: "5px 12px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  descriptionText: {
    fontSize: "12px",
    color: "#495057",
    lineHeight: "1.6",
    fontStyle: "italic",
    padding: "8px",
    backgroundColor: "white",
    borderLeft: "3px solid #3498db",
    borderRadius: "4px",
  },
  emptyCell: {
    color: "#95a5a6",
    fontStyle: "italic",
  },
  filesList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fileItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  fileIcon: {
    fontSize: "16px",
  },
  fileDownloadBtn: {
    padding: "4px 8px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "600",
    transition: "background-color 0.2s",
    textAlign: "left",
  },
  maxPointsBadge: {
    display: "inline-block",
    padding: "5px 10px",
    backgroundColor: "#8e44ad",
    color: "white",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "700",
  },
  scoreInput: {
    width: "70px",
    padding: "6px 8px",
    border: "2px solid #3498db",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
    textAlign: "center",
    color: "#2c3e50",
    backgroundColor: "white",
  },
  evidenceCheckbox: {
    width: "20px",
    height: "20px",
    cursor: "pointer",
    margin: "0 auto",
    display: "block",
  },
  notesInput: {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    fontSize: "12px",
    color: "#1a1a1a",
    backgroundColor: "#ffffff",
  },
  saveScoringButton: {
    padding: "10px 20px",
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "background-color 0.2s",
  },
  scoringTotals: {
    padding: "20px",
    backgroundColor: "#e8f5e9",
    borderRadius: "8px",
    marginBottom: "20px",
    border: "2px solid #27ae60",
  },
  totalsTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#27ae60",
    marginBottom: "15px",
  },
  totalsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "15px",
  },
  totalItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px",
    backgroundColor: "white",
    borderRadius: "6px",
  },
  totalLabel: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontWeight: "600",
    marginBottom: "5px",
  },
  totalValue: {
    fontSize: "16px",
    color: "#2c3e50",
    fontWeight: "700",
  },
  // Stili per gli allegati
  attachmentsList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  attachmentItem: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
  attachmentIconContainer: {
    flexShrink: 0,
  },
  attachmentIcon: {
    fontSize: "32px",
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentFileName: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "5px",
  },
  attachmentMeta: {
    fontSize: "13px",
    color: "#7f8c8d",
  },
  downloadButton: {
    padding: "10px 18px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    transition: "background-color 0.2s",
    flexShrink: 0,
  },
  downloadZipButton: {
    padding: "10px 20px",
    backgroundColor: "#9b59b6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "background-color 0.2s",
  },
  // Stili per KYC Status
  kycContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  kycStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  kycLabel: {
    fontSize: "14px",
    color: "#7f8c8d",
    fontWeight: "600",
  },
  kycStatusBadge: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  kycInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "15px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
  },
  kycInfoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  kycInfoLabel: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  kycInfoValue: {
    fontSize: "14px",
    color: "#2c3e50",
    fontWeight: "600",
    fontFamily: "monospace",
  },
  kycTimestamp: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontStyle: "italic",
  },
  kycReference: {
    fontSize: "12px",
    color: "#7f8c8d",
  },
  kycReferenceCode: {
    fontFamily: "monospace",
    color: "#495057",
    fontWeight: "600",
  },
  // Stili per Payment Status
  paymentSection: {
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    marginBottom: "15px",
    border: "1px solid #e9ecef",
  },
  paymentSectionTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#495057",
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  paymentQuoteHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  paymentAmount: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#2c3e50",
  },
  expiredBadge: {
    display: "inline-block",
    marginLeft: "10px",
    padding: "5px 12px",
    backgroundColor: "#e74c3c",
    color: "white",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
  },
  paymentInfoRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "8px",
  },
  paymentInfoLabel: {
    fontSize: "13px",
    color: "#7f8c8d",
    fontWeight: "600",
  },
  paymentInfoValue: {
    fontSize: "13px",
    color: "#2c3e50",
    fontWeight: "500",
  },
  paymentAddressRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "10px",
  },
  paymentAddress: {
    fontSize: "12px",
    fontFamily: "monospace",
    color: "#495057",
    backgroundColor: "white",
    padding: "6px 10px",
    borderRadius: "4px",
    border: "1px solid #dee2e6",
  },
  paymentStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "15px",
  },
  paymentStatusBadge: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  confirmationsContainer: {
    marginBottom: "15px",
  },
  confirmationsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  confirmationsLabel: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontWeight: "600",
  },
  confirmationsCount: {
    fontSize: "12px",
    color: "#2c3e50",
    fontWeight: "700",
    fontFamily: "monospace",
  },
  progressBarContainer: {
    width: "100%",
    height: "8px",
    backgroundColor: "#e9ecef",
    borderRadius: "4px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s ease",
  },
  txLink: {
    fontSize: "13px",
    color: "#3498db",
    fontFamily: "monospace",
    textDecoration: "none",
    fontWeight: "600",
    transition: "color 0.2s",
  },
  txLinkIcon: {
    fontSize: "12px",
  },
  paymentTimestamp: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontStyle: "italic",
    marginTop: "10px",
  },
  // Stili per Certification Status
  certificationContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  certificationStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  certificationLabel: {
    fontSize: "14px",
    color: "#7f8c8d",
    fontWeight: "600",
  },
  certificationStatusBadge: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  certificationButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  certificationDownloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    textDecoration: "none",
    transition: "background-color 0.2s",
  },
  certificationIpfsBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    backgroundColor: "#8b5cf6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    textDecoration: "none",
    transition: "background-color 0.2s",
  },
  certificationInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "15px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
    borderTop: "2px solid #dee2e6",
  },
  certificationInfoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  certificationInfoLabel: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  certificationInfoValue: {
    fontSize: "14px",
    color: "#2c3e50",
    fontWeight: "600",
  },
  certificationTimestamp: {
    fontSize: "12px",
    color: "#7f8c8d",
    fontStyle: "italic",
  },
  // Stili per la timeline dei commenti
  timeline: {
    position: "relative",
    paddingLeft: "30px",
  },
  timelineItem: {
    position: "relative",
    paddingBottom: "25px",
  },
  timelineDot: {
    position: "absolute",
    left: "-30px",
    top: "5px",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: "#3498db",
    border: "3px solid white",
    boxShadow: "0 0 0 2px #3498db",
  },
  timelineContent: {
    backgroundColor: "#f8f9fa",
    padding: "15px",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
  timelineHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    flexWrap: "wrap",
    gap: "10px",
  },
  actionTypeBadge: {
    display: "inline-block",
    padding: "5px 12px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  timelineDate: {
    fontSize: "13px",
    color: "#7f8c8d",
    fontWeight: "500",
  },
  timelineUser: {
    fontSize: "13px",
    color: "#495057",
    fontWeight: "600",
    marginBottom: "8px",
  },
  timelineNote: {
    fontSize: "14px",
    color: "#2c3e50",
    lineHeight: "1.6",
    fontStyle: "italic",
    padding: "10px",
    backgroundColor: "white",
    borderRadius: "4px",
    marginTop: "8px",
  },
  // Stili per il form commenti
  commentFormContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  commentTextarea: {
    width: "100%",
    padding: "15px",
    border: "2px solid #e9ecef",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
    transition: "border-color 0.2s",
  },
  commentButton: {
    alignSelf: "flex-start",
    padding: "12px 24px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "600",
    transition: "background-color 0.2s",
  },
  // Stili per le azioni
  notesContainer: {
    marginBottom: "20px",
  },
  notesLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "10px",
  },
  notesTextarea: {
    width: "100%",
    padding: "12px",
    border: "2px solid #e9ecef",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
    transition: "border-color 0.2s",
  },
  actionsButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  actionButton: {
    padding: "12px 24px",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "700",
    transition: "opacity 0.2s, transform 0.1s",
  },
  quickActionsContainer: {
    marginBottom: "25px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
  quickActionsLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: "700",
    color: "#2c3e50",
    marginBottom: "12px",
  },
  quickActionsButtons: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  quickActionButton: {
    padding: "10px 20px",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  dangerZone: {
    marginTop: "30px",
    padding: "20px",
    backgroundColor: "#fff5f5",
    borderRadius: "8px",
    border: "2px solid #e74c3c",
  },
  dangerZoneHeader: {
    fontSize: "14px",
    color: "#e74c3c",
    marginBottom: "15px",
    fontWeight: "700",
  },
  deleteRequestButton: {
    padding: "12px 24px",
    backgroundColor: "#e74c3c",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "background-color 0.2s",
  },
  certifySection: {
    marginTop: "25px",
    marginBottom: "25px",
    padding: "25px",
    backgroundColor: "#f0f3ff",
    borderRadius: "8px",
    border: "2px solid #8e44ad",
  },
  certifySectionTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#8e44ad",
    marginBottom: "10px",
  },
  certifySectionDesc: {
    fontSize: "14px",
    color: "#2c3e50",
    marginBottom: "15px",
    lineHeight: "1.6",
  },
  certificationMethodsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
    marginTop: "20px",
  },
  certificationMethod: {
    padding: "20px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  },
  methodTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "8px",
    marginTop: "0",
  },
  methodDescription: {
    fontSize: "13px",
    color: "#64748b",
    lineHeight: "1.6",
    marginBottom: "16px",
  },
  decertifySection: {
    marginTop: "25px",
    marginBottom: "25px",
    padding: "25px",
    backgroundColor: "#fef2f2",
    borderRadius: "8px",
    border: "2px solid #dc2626",
  },
  decertifySectionTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#dc2626",
    marginBottom: "10px",
  },
  decertifySectionDesc: {
    fontSize: "14px",
    color: "#7f1d1d",
    marginBottom: "15px",
    lineHeight: "1.6",
  },
  finalStatusMessage: {
    textAlign: "center",
    fontSize: "18px",
    color: "#7f8c8d",
    padding: "30px",
    fontWeight: "500",
  },
  noDataMessage: {
    textAlign: "center",
    fontSize: "15px",
    color: "#95a5a6",
    padding: "30px",
    fontStyle: "italic",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
};

export default AdminESGDetail;
